import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  Badge,
  ProgressBar,
} from "react-bootstrap";
import axios from "axios";
import { Authorization } from "../Context/Authorization";
import { toast } from "react-toastify";
import Preloader from "../shared/PreLoaders";

const Api = process.env.REACT_APP_API;

const CircleApproval = ({ open, onClose }) => {
  const [divisions, setDivisions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittedCircleIds, setSubmittedCircleIds] = useState([]);
  const [selectedDivisions, setSelectedDivisions] = useState([]);
  const [selectedJfmcTypeId, setSelectedJfmcTypeId] = useState(null);
  const [jfmcTypes, setJfmcTypes] = useState([]);
  // ✅ Automatically use the previous month
  const getPreviousMonth = () => {
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    return `${year}-${month.toString().padStart(2, "0")}`;
  };
  const formatSelectedMonth = () => {
    if (!selectedMonth) return "";
    const date = new Date(`${selectedMonth}-01`);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
  };
  useEffect(() => {
    if (open) setSelectedMonth(getPreviousMonth());
  }, [open]);

  // ✅ Load divisions and submission status
  useEffect(() => {
    if (!open || !selectedMonth) return;

    const fetchCircleStatuses = async () => {
      try {
        setLoading(true);
        const config = Authorization();

        const statusRes = await axios.get(
          `${Api}/api/declaration/check-circle-status?month=${selectedMonth}`,
          config
        );

        const circleList = statusRes.data.circles || [];

        setDivisions(circleList); // keep name same if you want

        // 🔹 Extract unique JFMC types
        const allTypes = circleList.flatMap(c => c.jfmcTypes || []);
        const uniqueMap = {};

        allTypes.forEach(type => {
          if (!uniqueMap[type.jfmcTypeId]) {
            uniqueMap[type.jfmcTypeId] = {
              jfmcTypeId: type.jfmcTypeId,
              jfmcTypeName: type.jfmcTypeName,
            };
          }
        });

        const uniqueTypes = Object.values(uniqueMap);
        setJfmcTypes(uniqueTypes);

        if (uniqueTypes.length > 0) {
          setSelectedJfmcTypeId(uniqueTypes[0].jfmcTypeId);
        }

      } catch (err) {
        console.error("Error loading circles:", err);
        toast.error("Failed to fetch circle statuses");
      } finally {
        setLoading(false);
      }
    };

    fetchCircleStatuses();
  }, [open, selectedMonth]);

  // ✅ Checkbox handler
  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setSelectedDivisions((prev) =>
      checked ? [...prev, value] : prev.filter((id) => id !== value)
    );
  };

  // ✅ Handle Circle Submission
  const handleSubmit = async () => {
    // Check if already submitted
    const selectedCircles = [
      ...new Set(
        divisions
          .filter((d) => selectedDivisions.includes(d._id))
          .map((d) => d.circleId)
      ),
    ];

    if (selectedCircles.some((id) => submittedCircleIds.includes(id))) {
      toast.warning("This circle has already been submitted for this month.");
      return;
    }

    if (divisions.some((d) => !d.allDivisionsSubmitted)) {
      toast.warning("Cannot submit. Some divisions are incomplete.");
      return;
    }

    if (selectedDivisions.length === 0) {
      toast.warning("Please select at least one division.");
      return;
    }

    if (selectedCircles.length > 1) {
      toast.warning(
        "You have selected divisions from multiple circles. Please select divisions from only one circle at a time."
      );
      return;
    }

    const circleId = selectedCircles[0];
    if (!circleId) {
      toast.error("Missing Circle ID — cannot submit.");
      return;
    }

    if (!window.confirm(`Submit Circle for ${selectedMonth}?`)) return;

    try {
      const config = Authorization();
      const payload = {
        month: selectedMonth,
        circleId,
        divisionIds: selectedDivisions,
      };

      const res = await axios.post(
        `${Api}/api/declaration/circle/submit`,
        payload,
        config
      );

      toast.success(res.data.message || "Circle submitted successfully!");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || "Submission failed");
    }
  };
  const filteredCircles = divisions.filter(c =>
    c.jfmcTypes?.some(
      t => t.jfmcTypeId === selectedJfmcTypeId
    )
  );
  // ✅ Progress Calculation
  const totalCircles = filteredCircles.length;

  const completedCircles = filteredCircles.filter(c => {
    const type = c.jfmcTypes?.find(
      t => t.jfmcTypeId === selectedJfmcTypeId
    );
    return type?.allVfcsSubmitted;
  }).length;

  const progress = totalCircles
    ? (completedCircles / totalCircles) * 100
    : 0;

  // ✅ Get already submitted circle names
  const submittedCircleNames = [
    ...new Set(
      divisions
        .filter((d) => submittedCircleIds.includes(d.circleId))
        .map((d) => d.circleName)
    ),
  ];

  // ✅ Determine if all circles in view are already submitted
  const allCirclesAlreadySubmitted =
    submittedCircleNames.length > 0 &&
    submittedCircleNames.length ===
    new Set(divisions.map((d) => d.circleId)).size;
  const readyDivisionsCount = filteredCircles.filter(d => {
    const type = d.jfmcTypes?.find(
      t => t.jfmcTypeId === selectedJfmcTypeId
    );
    return type?.allVfcsSubmitted && !d.circleAlreadySubmitted;
  }).length;

  const allSelected =
    readyDivisionsCount > 0 &&
    selectedDivisions.length === readyDivisionsCount;

  const handleSelectAll = (e) => {
    const { checked } = e.target;

    if (checked) {
      const readyIds = filteredCircles
        .filter(d => {
          const type = d.jfmcTypes?.find(
            t => t.jfmcTypeId === selectedJfmcTypeId
          );
          return type?.allVfcsSubmitted && !d.circleAlreadySubmitted;
        })
        .map(d => d._id);

      setSelectedDivisions(readyIds);
    } else {
      setSelectedDivisions([]);
    }
  };
  return (
    <Modal show={open} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Circle Approval</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group>
          <Form.Label>Selected Month</Form.Label>
          <Form.Control type="month" value={selectedMonth} disabled />
        </Form.Group>

        <hr />

        {loading ? (
          <div className="text-center">
            {loading && <Preloader loading={loading} />}
          </div>
        ) : (
          <>
            <Alert variant="primary" className="text-center">
              By selecting the Division(s) within your Circle, you hereby confirm that all records
              pertaining to these Divisions for the month <strong>{formatSelectedMonth()}</strong> have
              been duly updated in the web portal.
            </Alert>
            {submittedCircleNames.length > 0 && (
              <Alert variant="info" className="text-center">
                ✅ The following circle(s) already submitted for{" "}
                <strong>{selectedMonth}</strong>:{" "}
                <strong>{submittedCircleNames.join(", ")}</strong>
              </Alert>
            )}

            <div className="mb-3">
              <strong>
                Progress: {completedCircles}/{totalCircles} Circles Completed
              </strong>
              <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
            </div>

            {/* 🔘 JFMC TYPE BUTTONS */}
            {jfmcTypes.length > 0 && (
              <div className="d-flex gap-2 mb-3 justify-content-center">
                {jfmcTypes.map((type) => (
                  <div className="jfmc-switch">
                    <button
                      className={`btn ${selectedJfmcTypeId?.jfmcTypeId === type.jfmcTypeId ? "btn-success" : "btn-outline-secondary"}`}
                      key={type.jfmcTypeId}
                      onClick={() => {
                        setSelectedJfmcTypeId(type.jfmcTypeId);
                        setSelectedDivisions([]);
                      }}
                    >
                      {type.jfmcTypeName}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* ✅ Select All */}
            {!allCirclesAlreadySubmitted && filteredCircles.length > 0 && (
              <div className="d-flex justify-content-between align-items-center border-bottom py-2 bg-light px-2 rounded mb-2">
                <Form.Check
                  type="checkbox"
                  label="Select All Ready Divisions"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
                <Badge bg={allSelected ? "success" : "secondary"}>
                  {allSelected
                    ? "All Selected"
                    : `${selectedDivisions.length}/${readyDivisionsCount} Selected`}
                </Badge>
              </div>
            )}

            {divisions.length === 0 ? (
              <p className="text-muted text-center">No divisions found.</p>
            ) : (
              filteredCircles
                .sort((a, b) => a.circleName.localeCompare(b.circleName))
                .map(c => {

                  const selectedType = c.jfmcTypes?.find(
                    t => t.jfmcTypeId === selectedJfmcTypeId
                  );

                  return (
                    <div
                      key={c.circleId}
                      className="d-flex justify-content-between align-items-center border-bottom py-2"
                    >
                      <div>
                        <Form.Check
                          type="checkbox"
                          label={c.circleName}
                          value={c.circleId}
                          checked={selectedDivisions.includes(c.circleId)}
                          onChange={handleCheckboxChange}
                          disabled={!selectedType?.allVfcsSubmitted}
                        />

                        <small className="ms-4">
                          {selectedType?.submittedVfcs || 0}/
                          {selectedType?.totalVfcs || 0}
                        </small>
                      </div>

                      <Badge
                        bg={
                          selectedType?.allVfcsSubmitted
                            ? "success"
                            : "warning"
                        }
                      >
                        {selectedType?.allVfcsSubmitted
                          ? "Ready"
                          : "Incomplete"}
                      </Badge>
                    </div>
                  );
                })
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit}
          disabled={
            selectedDivisions.length === 0 || allCirclesAlreadySubmitted
          }
        >
          Submit Circle
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CircleApproval;
